import { Fragment, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { DeletedUserBadge } from "@/components/DeletedUserBadge";
import { useAuth } from "@/contexts/AuthContext";
import { Send, Paperclip, MessageSquare, Users, User, Search, X } from "lucide-react";
import { uploadFile, fileSrc } from "@/lib/uploads";
import { roleLabel } from "@/lib/roles";

/** Une dos listas de mensajes deduplicando por id y ordenando por created_at. */
function mergeById(prev, incoming) {
  const byId = new Map();
  for (const msg of prev) byId.set(msg.id, msg);
  for (const msg of incoming) byId.set(msg.id, msg);
  return Array.from(byId.values()).sort((a, b) =>
    (a.created_at || "") < (b.created_at || "") ? -1 : (a.created_at || "") > (b.created_at || "") ? 1 : 0
  );
}

// Fix BQ (2026-11-03): el chat solo mostraba la hora de cada mensaje, nunca la fecha.
const CHAT_TZ = "America/Argentina/Buenos_Aires";
const chatDayKey = (iso) => new Date(iso).toLocaleDateString("en-CA", { timeZone: CHAT_TZ });
function chatDayLabel(iso) {
  const key = chatDayKey(iso);
  const today = chatDayKey(new Date().toISOString());
  const yesterday = chatDayKey(new Date(Date.now() - 86400000).toISOString());
  if (key === today) return "Hoy";
  if (key === yesterday) return "Ayer";
  return new Date(iso).toLocaleDateString("es-AR", { timeZone: CHAT_TZ, weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" });
}

/** Formatea la hora del último mensaje: hoy = HH:MM, otro día = DD/MM. */
function formatChatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit" });
}

/** Texto de vista previa del último mensaje, con adjuntos sin emoji. */
function previewText(lastMsg) {
  if (!lastMsg) return "Sin mensajes todavía";
  const who = lastMsg.mine ? "Vos" : lastMsg.user_name;
  let body = lastMsg.text;
  if (!body) body = lastMsg.attachment_kind === "image" ? "Imagen adjunta" : lastMsg.attachment_kind === "video" ? "Video adjunto" : "Adjunto";
  return `${who}: ${body}`;
}

/** Lista de canales (groups + dms). Llama onSelect(channel). Poll cada 8s
 * para mantener actualizado quién escribe, la vista previa y lo sin leer. */
export function ChatChannelList({ onSelect, activeId, onData }) {
  const [data, setData] = useState({ groups: [], dms: [] });
  const [search, setSearch] = useState("");
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try { const r = await api.get("/chats/list"); if (!cancelled) { setData(r.data); onData?.(r.data); } }
      catch (e) { if (!cancelled) toast.error(formatApiError(e)); }
    };
    load();
    const it = setInterval(load, 8000);
    return () => { cancelled = true; clearInterval(it); };
  }, []);
  const q = search.trim().toLowerCase();
  const groups = q ? data.groups.filter((c) => (c.name || "").toLowerCase().includes(q)) : data.groups;
  const dms = q ? data.dms.filter((c) => (c.name || "").toLowerCase().includes(q)) : data.dms;
  return (
    <div data-testid="chat-channel-list">
      <div style={{ position: "relative", padding: "10px 14px 4px" }}>
        <Search size={14} style={{ position: "absolute", left: 24, top: "50%", transform: "translateY(-50%)", color: "var(--invict-text-muted)" }} />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar objetivo..."
          data-testid="chats-search"
          style={{ width: "100%", padding: "8px 30px", fontSize: 12, border: "1.5px solid var(--invict-bone-3)", borderRadius: 20 }}
        />
        {search && (
          <button type="button" onClick={() => setSearch("")} data-testid="chats-search-clear"
            style={{ position: "absolute", right: 22, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--invict-text-muted)", padding: 4 }}>
            <X size={13} />
          </button>
        )}
      </div>
      <Section title="Objetivos" icon={Users}>
        {groups.length === 0 && <Empty label={q ? "Sin resultados" : "Sin objetivos asignados"} />}
        {groups.map((c) => (
          <ChannelRow key={c.channel_id} ch={c} active={c.channel_id === activeId} onSelect={onSelect} />
        ))}
      </Section>
      <Section title="Mensajes directos" icon={User}>
        {dms.length === 0 && <Empty label={q ? "Sin resultados" : "Sin contactos"} />}
        {dms.map((c) => (
          <ChannelRow key={c.channel_id} ch={c} active={c.channel_id === activeId} onSelect={onSelect} />
        ))}
      </Section>
    </div>
  );
}

function Section({ title, icon: Ic, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--invict-text-muted)", padding: "10px 14px 6px" }}>
        <Ic size={11} style={{ display: "inline", marginRight: 6 }} />{title}
      </div>
      {children}
    </div>
  );
}
function Empty({ label }) { return <div style={{ padding: "10px 14px", fontSize: 12, color: "var(--invict-text-muted)" }}>{label}</div>; }

function ChannelRow({ ch, active, onSelect }) {
  const unread = ch.unread_count > 0;
  return (
    <button
      onClick={() => onSelect(ch)}
      data-testid={`chat-channel-${ch.channel_id}`}
      style={{
        width: "100%", textAlign: "left", padding: "11px 14px",
        background: active ? "rgba(191,52,55,0.08)" : unread ? "rgba(191,52,55,0.045)" : "transparent",
        borderLeft: active ? "3px solid #BF3437" : "3px solid transparent",
        border: "none", cursor: "pointer", color: "#0A0A0A", fontWeight: 700, fontSize: 13,
        display: "flex", alignItems: "center", gap: 10,
      }}>
      <div style={{ width: 30, height: 30, borderRadius: "50%", background: active ? "#BF3437" : "var(--invict-bone-3)", color: active ? "#fff" : "#0A0A0A", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 13, flexShrink: 0 }}>
        {(ch.name || "?").charAt(0).toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
          <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: unread ? 900 : 700 }}>{ch.name}</div>
          {ch.last_message?.created_at && (
            <div style={{ fontSize: 10, color: unread ? "#BF3437" : "var(--invict-text-muted)", fontWeight: unread ? 800 : 600, flexShrink: 0 }}>{formatChatTime(ch.last_message.created_at)}</div>
          )}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <div data-testid={`chat-channel-preview-${ch.channel_id}`} style={{ fontSize: 11, color: unread ? "#0A0A0A" : "var(--invict-text-muted)", fontWeight: unread ? 700 : 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
            {previewText(ch.last_message)}
          </div>
          {unread && (
            <span data-testid={`chat-unread-${ch.channel_id}`} style={{ background: "#BF3437", color: "#fff", borderRadius: 10, minWidth: 18, height: 18, fontSize: 10, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 5px", flexShrink: 0 }}>
              {ch.unread_count > 99 ? "99+" : ch.unread_count}
            </span>
          )}
        </div>
        <div style={{ fontSize: 10, color: "var(--invict-text-muted)", fontWeight: 600 }}>
          {ch.kind === "group" ? "Objetivo · Grupal" : `Directo · ${roleLabel(ch.other_role)}`}
        </div>
      </div>
    </button>
  );
}

/** Conversación de un canal. polling cada 4s. */
export function ChatRoom({ channel }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [attach, setAttach] = useState(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const endRef = useRef(null);
  const lastAtRef = useRef(null);

  useEffect(() => {
    if (!channel) return;
    let cancelled = false;
    lastAtRef.current = null;
    const fetchAll = async () => {
      try {
        const lastAt = lastAtRef.current;
        const url = lastAt ? `/chats/${encodeURIComponent(channel.channel_id)}/messages?since=${encodeURIComponent(lastAt)}` : `/chats/${encodeURIComponent(channel.channel_id)}/messages`;
        const r = await api.get(url);
        if (cancelled) return;
        setMsgs((prev) => mergeById(lastAt ? prev : [], r.data));
        if (r.data.length) {
          const newest = r.data[r.data.length - 1].created_at;
          if (!lastAtRef.current || newest > lastAtRef.current) lastAtRef.current = newest;
        }
      } catch {/* silent */}
    };
    setMsgs([]);
    fetchAll();
    const it = setInterval(fetchAll, 4000);
    return () => { cancelled = true; clearInterval(it); };
  }, [channel?.channel_id]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  const send = async (e) => {
    e?.preventDefault();
    if (sending) return;
    if (!text.trim() && !attach) return;
    setSending(true);
    try {
      const body = { channel_id: channel.channel_id, text: text.trim() || null };
      if (attach) { body.attachment_url = attach.url; body.attachment_kind = attach.kind; }
      const r = await api.post("/chats/messages", body);
      setMsgs((m) => mergeById(m, [r.data]));
      if (r.data?.created_at && (!lastAtRef.current || r.data.created_at > lastAtRef.current)) {
        lastAtRef.current = r.data.created_at;
      }
      setText(""); setAttach(null);
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setSending(false); }
  };

  const pickFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const r = await uploadFile(f);
      setAttach({ url: r.url, kind: r.kind });
    } catch (err) { toast.error("No pude subir el archivo"); }
    e.target.value = "";
  };

  if (!channel) {
    return (
      <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--invict-text-muted)", padding: 40, flexDirection: "column", gap: 10 }}>
        <MessageSquare size={40} style={{ opacity: 0.3 }} />
        <div style={{ fontSize: 13, fontWeight: 700 }}>Elegí un chat para comenzar</div>
      </div>
    );
  }

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", background: "var(--invict-bone-2)" }} data-testid="chat-room">
      <div style={{ background: "#0A0A0A", color: "#FFFBF4", padding: "14px 18px", borderBottom: "2px solid #BF3437" }}>
        <div style={{ fontSize: 10, letterSpacing: "0.2em", color: "#BF3437", fontWeight: 800, textTransform: "uppercase" }}>{channel.kind === "group" ? "Objetivo grupal" : "Mensaje directo"}</div>
        <div style={{ fontSize: 16, fontWeight: 900 }}>{channel.name}</div>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: 16 }} data-testid="chat-messages">
        {msgs.length === 0 && (
          <div style={{ textAlign: "center", padding: 40, color: "var(--invict-text-muted)", fontSize: 12 }}>Sin mensajes todavía. Empezá la conversación.</div>
        )}
        {msgs.map((m, idx) => {
          const mine = m.user_id === user?.id;
          const newDay = idx === 0 || chatDayKey(m.created_at) !== chatDayKey(msgs[idx - 1].created_at);
          return (
            <Fragment key={m.id}>
            {newDay && (
              <div style={{ display: "flex", justifyContent: "center", margin: "10px 0 8px" }} data-testid={`chat-day-${chatDayKey(m.created_at)}`}>
                <span style={{ fontSize: 10, fontWeight: 800, textTransform: "capitalize", letterSpacing: "0.04em", background: "var(--invict-bone-3)", color: "#0A0A0A", padding: "3px 10px", borderRadius: 10 }}>{chatDayLabel(m.created_at)}</span>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", marginBottom: 8 }}>
              <div style={{ maxWidth: "75%" }}>
                {!mine && <div style={{ fontSize: 10, fontWeight: 700, color: "var(--invict-text-muted)", marginBottom: 2, letterSpacing: "0.05em" }}>{m.user_name}{m.user_deleted && <DeletedUserBadge id={m.id} />} <span style={{ color: "#BF3437" }}>· {roleLabel(m.user_role)}</span></div>}
                <div style={{
                  background: mine ? "#0A0A0A" : "#FFFBF4",
                  color: mine ? "#FFFBF4" : "#0A0A0A",
                  padding: "10px 14px", borderRadius: 12,
                  borderTopLeftRadius: mine ? 12 : 2,
                  borderTopRightRadius: mine ? 2 : 12,
                  fontSize: 13, lineHeight: 1.4,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                }}>
                  {m.text && <div>{m.text}</div>}
                  {m.attachment_url && (
                    m.attachment_kind === "image" ? (
                      <a href={fileSrc(m.attachment_url)} target="_blank" rel="noreferrer"><img src={fileSrc(m.attachment_url)} alt="" style={{ maxWidth: 220, marginTop: m.text ? 6 : 0, borderRadius: 4, display: "block" }} /></a>
                    ) : (
                      <video src={fileSrc(m.attachment_url)} controls style={{ maxWidth: 240, marginTop: m.text ? 6 : 0, borderRadius: 4, display: "block" }} />
                    )
                  )}
                  <div style={{ fontSize: 9, opacity: 0.6, marginTop: 4, textAlign: "right" }} title={new Date(m.created_at).toLocaleString("es-AR", { timeZone: CHAT_TZ })}>{new Date(m.created_at).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}</div>
                </div>
              </div>
            </div>
            </Fragment>
          );
        })}
        <div ref={endRef} />
      </div>
      {attach && (
        <div style={{ padding: "8px 14px", background: "#FFFBF4", borderTop: "1px solid var(--invict-bone-3)", fontSize: 11, color: "var(--invict-text-muted)" }}>
          📎 Adjunto listo · <button type="button" onClick={() => setAttach(null)} style={{ background: "none", border: "none", color: "#BF3437", cursor: "pointer", fontWeight: 700 }}>quitar</button>
        </div>
      )}
      <form onSubmit={send} style={{ display: "flex", gap: 6, padding: 10, background: "#fff", borderTop: "1px solid var(--invict-bone-3)" }}>
        <input ref={fileRef} type="file" accept="image/*,video/mp4" onChange={pickFile} style={{ display: "none" }} data-testid="chat-file-input" />
        <button type="button" onClick={() => fileRef.current?.click()} style={{ background: "none", border: "none", padding: 10, cursor: "pointer", color: "var(--invict-text-muted)" }} data-testid="chat-attach"><Paperclip size={18} /></button>
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Escribí un mensaje..." style={{ flex: 1, border: "1px solid var(--invict-bone-3)", borderRadius: 20, padding: "10px 14px", fontSize: 13, outline: "none" }} data-testid="chat-input" />
        <button type="submit" disabled={sending || (!text.trim() && !attach)} className="btn-primary" style={{ padding: "10px 16px", borderRadius: 20 }} data-testid="chat-send"><Send size={14} /></button>
      </form>
    </div>
  );
}
